import { useState } from 'react';
import { UserPlus, XCircle, AlertCircle, CheckCircle2, Users, AlertTriangle } from 'lucide-react';
import {
  useGetClaimAccessGrants,
  getGetClaimAccessGrantsQueryKey,
  useCreateClaimAccessGrant,
  useRevokeClaimAccessGrant,
  type ClaimAccessGrant,
} from '@edison/api-client-react';
import { useAuthStore } from '@/store/auth-store.js';
import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';

interface Props {
  claimId: string;
}

const MANAGE_ROLES = new Set(['tenant_admin', 'r_and_d_manager']);

const ROLE_LABEL: Record<string, string> = {
  auditor: 'Auditor',
  advisor: 'Advisor',
  due_diligence: 'Due Diligence',
};

// Mirrors ROLE_MAX_DAYS in artifacts/api-server/src/routes/claim-access-grants.ts. The backend
// is the source of truth for the cap — this only sets sensible UI defaults and clamps the input
// so the user can't submit something the API will reject.
const ROLE_EXPIRY: Record<'auditor' | 'advisor' | 'due_diligence', { def: number; max: number }> = {
  auditor: { def: 60, max: 90 },
  advisor: { def: 30, max: 60 },
  due_diligence: { def: 14, max: 30 },
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function formatExpiry(iso: string) {
  return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

function extractErrorMessage(err: unknown, fallback: string): string {
  const message = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
    ?.message;
  return message ?? fallback;
}

function GrantRow({
  grant,
  onRevoke,
  isRevoking,
}: {
  grant: ClaimAccessGrant;
  onRevoke: (grant: ClaimAccessGrant) => void;
  isRevoking: boolean;
}) {
  const [confirming, setConfirming] = useState(false);
  const isActive = grant.status === 'active';

  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium">{grant.email ?? 'Unknown reviewer'}</p>
          <Badge variant="info">{ROLE_LABEL[grant.role] ?? grant.role}</Badge>
          {grant.status === 'active' && (
            <Badge variant="positive">
              <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
              Active
            </Badge>
          )}
          {grant.status === 'revoked' && (
            <Badge variant="destructive">
              <XCircle className="h-3 w-3" aria-hidden="true" />
              Revoked
            </Badge>
          )}
          {grant.status === 'expired' && (
            <Badge variant="default">
              <AlertCircle className="h-3 w-3" aria-hidden="true" />
              Expired
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground">expires {formatExpiry(grant.expiresAt)}</p>
      </div>
      {isActive &&
        (confirming ? (
          <div className="flex items-center gap-1.5 rounded-md border border-destructive/50 bg-destructive/5 px-2 py-1 shrink-0">
            <span className="text-xs text-destructive">Revoke access?</span>
            <button
              onClick={() => {
                onRevoke(grant);
                setConfirming(false);
              }}
              disabled={isRevoking}
              className="h-5 px-2 rounded bg-destructive text-destructive-foreground text-xs font-medium hover:bg-destructive/90 transition-colors disabled:opacity-50"
            >
              {isRevoking ? 'Revoking…' : 'Yes, revoke'}
            </button>
            <button
              onClick={() => setConfirming(false)}
              className="h-5 w-5 rounded text-muted-foreground hover:bg-accent flex items-center justify-center"
              aria-label="Cancel revoke"
            >
              <XCircle className="h-3 w-3" aria-hidden="true" />
            </button>
          </div>
        ) : (
          <Button variant="destructive" size="sm" onClick={() => setConfirming(true)} className="shrink-0">
            <XCircle className="h-3.5 w-3.5" aria-hidden="true" />
            Revoke
          </Button>
        ))}
    </div>
  );
}

export function ReviewersPanel({ claimId }: Props) {
  const { user } = useAuthStore();
  const canManage = user?.role ? MANAGE_ROLES.has(user.role) : false;

  const { data, isLoading, error, refetch } = useGetClaimAccessGrants(
    { claimId },
    { query: { enabled: canManage, queryKey: getGetClaimAccessGrantsQueryKey({ claimId }) } },
  );
  const { mutate: createGrant, isPending: isInviting } = useCreateClaimAccessGrant();
  const { mutate: revokeGrant, isPending: isRevoking } = useRevokeClaimAccessGrant();

  const [showForm, setShowForm] = useState(false);
  const [formEmail, setFormEmail] = useState('');
  const [formRole, setFormRole] = useState<'auditor' | 'advisor' | 'due_diligence'>('auditor');
  const [formDays, setFormDays] = useState(ROLE_EXPIRY.auditor.def);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [lastInvite, setLastInvite] = useState<ClaimAccessGrant | null>(null);

  const onRoleChange = (role: 'auditor' | 'advisor' | 'due_diligence') => {
    setFormRole(role);
    setFormDays(ROLE_EXPIRY[role].def);
  };
  const roleMaxDays = ROLE_EXPIRY[formRole].max;
  const emailValid = EMAIL_RE.test(formEmail.trim());

  const handleInvite = () => {
    if (!emailValid) return;
    setInviteError(null);
    const expiresAt = new Date(Date.now() + formDays * 24 * 60 * 60 * 1000).toISOString();
    createGrant(
      { data: { claimId, email: formEmail.trim(), role: formRole, expiresAt } },
      {
        onSuccess: (res) => {
          setLastInvite(res.data ?? null);
          setShowForm(false);
          setFormEmail('');
          void refetch();
        },
        onError: (err) => {
          setInviteError(extractErrorMessage(err, 'Failed to invite reviewer.'));
        },
      },
    );
  };

  const handleRevoke = (grant: ClaimAccessGrant) => {
    revokeGrant({ id: grant.id }, { onSuccess: () => void refetch() });
  };

  if (!canManage) {
    return null;
  }

  const grants = data?.data?.items ?? [];
  const hasMagicLink = !!lastInvite && !!(lastInvite as ClaimAccessGrant & { reviewerMagicLink?: string }).reviewerMagicLink;
  const magicLink = hasMagicLink ? (lastInvite as ClaimAccessGrant & { reviewerMagicLink?: string }).reviewerMagicLink! : null;
  const showClerkFailureBanner = !!lastInvite && lastInvite.clerkInviteSent === false && !hasMagicLink;
  const showMagicLinkBanner = !!lastInvite && lastInvite.clerkInviteSent === false && hasMagicLink;
  const showInviteSuccessBanner = !!lastInvite && lastInvite.clerkInviteSent !== false;
  // `error` from the generated hook is typed `unknown` (TError defaults to unknown) — `unknown`
  // can't be narrowed by `&&`, so coerce to boolean before using it as a JSX condition (bare
  // `error && (...)` would type the whole expression as `unknown`, not `false | ReactNode`).
  const hasError = !!error;

  return (
    <section className="rounded-lg border p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <h2 className="font-medium text-sm">Reviewers</h2>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            setShowForm(true);
            setLastInvite(null);
            setInviteError(null);
          }}
        >
          <UserPlus className="h-3.5 w-3.5" aria-hidden="true" />
          Invite reviewer
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        Invited reviewers sign in with their own account and have full access — evidence, AI classification
        trail, eligibility analysis, and expenditure. The role controls how long access lasts: Auditor up
        to 90 days, Advisor up to 60 days, Due Diligence up to 30 days.
      </p>

      {/* Invite result banner */}
      {showMagicLinkBanner && (
        <div className="flex items-start gap-2 rounded-lg border border-blue-300 bg-blue-50 p-3 text-xs text-blue-900">
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden="true" />
          <div className="space-y-1.5">
            <p className="font-medium">
              Access granted. Copy this magic link and send it to {lastInvite?.email}:
            </p>
            <div className="font-mono bg-blue-100 rounded px-2 py-1 break-all select-all">
              {magicLink}
            </div>
            <p className="text-blue-700">
              This link signs them in directly — no password needed. Valid for 7 days. Do not share it publicly.
            </p>
          </div>
        </div>
      )}
      {showClerkFailureBanner && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden="true" />
          <div className="space-y-1.5">
            <p>
              Reviewer access was granted, but the invitation could not be sent
              {lastInvite?.clerkInviteError ? `: ${lastInvite.clerkInviteError}` : '.'}{' '}
            </p>
          </div>
        </div>
      )}
      {showInviteSuccessBanner && (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          Reviewer invited — an invitation email was sent to {lastInvite?.email}.
        </div>
      )}

      {hasError && (
        <div className="flex items-center gap-2 text-destructive text-xs">
          <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
          Failed to load reviewers.
          <button className="underline" onClick={() => void refetch()}>
            Retry
          </button>
        </div>
      )}

      {/* Invite form */}
      {showForm && (
        <div className="rounded-lg border p-4 space-y-3">
          <div className="space-y-2">
            <label className="block text-xs font-medium" htmlFor="reviewer-email">
              Email
            </label>
            <input
              id="reviewer-email"
              type="email"
              value={formEmail}
              onChange={(e) => setFormEmail(e.target.value)}
              placeholder="reviewer@example.com"
              className="w-full rounded border px-2 py-2 text-sm"
            />
            {formEmail.length > 0 && !emailValid && (
              <p className="text-xs text-destructive">Enter a valid email address.</p>
            )}
          </div>
          <div className="space-y-2">
            <label className="block text-xs font-medium" htmlFor="reviewer-role">
              Role
            </label>
            <select
              id="reviewer-role"
              value={formRole}
              onChange={(e) => onRoleChange(e.target.value as typeof formRole)}
              className="w-full rounded border px-2 py-2 text-sm"
            >
              <option value="auditor">Auditor (max 90 days)</option>
              <option value="advisor">Advisor (max 60 days)</option>
              <option value="due_diligence">Due Diligence (max 30 days)</option>
            </select>
          </div>
          <div className="space-y-2">
            <label className="block text-xs font-medium" htmlFor="reviewer-days">
              Expiry (days, max {roleMaxDays})
            </label>
            <input
              id="reviewer-days"
              type="number"
              min={1}
              max={roleMaxDays}
              value={formDays}
              onChange={(e) => setFormDays(Math.min(roleMaxDays, Math.max(1, parseInt(e.target.value) || 1)))}
              className="w-24 rounded border px-2 py-2 text-sm"
            />
          </div>
          {inviteError && (
            <div className="flex items-center gap-2 text-destructive text-xs">
              <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
              {inviteError}
            </div>
          )}
          <div className="flex gap-2">
            <Button variant="primary" size="sm" onClick={handleInvite} disabled={isInviting || !emailValid}>
              {isInviting ? 'Inviting…' : 'Send invite'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {isLoading && <div data-testid="reviewers-panel-loading" className="h-16 w-full rounded-lg skeleton" />}

      {/* Grants list */}
      {!isLoading && grants.length > 0 && (
        <div className="space-y-2">
          {grants.map((g) => (
            <GrantRow key={g.id} grant={g} onRevoke={handleRevoke} isRevoking={isRevoking} />
          ))}
        </div>
      )}
      {!isLoading && grants.length === 0 && !hasError && (
        <p className="text-sm text-muted-foreground text-center py-4">No reviewers invited yet.</p>
      )}
    </section>
  );
}
